// Analytics facade around window.posthog. All events go through here so we can
// (a) no-op safely when PostHog isn't loaded and (b) strip any PII defensively.

const BLOCKED_KEYS = new Set([
  "bet",
  "bet_amount",
  "wager",
  "odds",
  "leg_odds",
  "american_odds",
  "decimal_odds",
  "fractional_odds",
  "email",
  "name",
  "full_name",
  "phone",
  "phone_number",
  "ip",
  "ip_address",
  "user",
  "user_email",
  "user_name",
]);

const sanitize = (props) => {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    if (BLOCKED_KEYS.has(String(k).toLowerCase())) continue;
    // Also drop obviously-numeric keys that might contain sensitive amounts.
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
};

/**
 * Fire a PostHog event with sanitized properties.
 * Never throws — analytics must never break the app.
 */
export const track = (event, properties = {}) => {
  try {
    if (typeof window === "undefined") return;
    const ph = window.posthog;
    if (!ph || typeof ph.capture !== "function") return;

    const safe = sanitize(properties);
    if (!safe.timestamp) safe.timestamp = new Date().toISOString();
    if (!safe.current_page && typeof window.location !== "undefined") {
      safe.current_page = window.location.pathname;
    }

    ph.capture(event, safe);
  } catch {
    /* swallow — analytics is best-effort */
  }
};

/**
 * Convenience for sponsor-related events which share a payload shape.
 */
export const trackSponsor = (event, sponsor, elementClicked) => {
  track(event, {
    sponsor_id: sponsor.id,
    sponsor_name: sponsor.name,
    sponsor_url: sponsor.url,
    element_clicked: elementClicked,
  });
};

export const EVENTS = {
  // Sponsor
  SPONSOR_CARD_CLICKED: "sponsor_card_clicked",
  SPONSOR_LOGO_CLICKED: "sponsor_logo_clicked",
  VISIT_SPONSOR_CLICKED: "visit_sponsor_clicked",
  ADVERTISE_WITH_US_CLICKED: "advertise_with_us_clicked",
  // Calculator / converter
  PARLAY_CALCULATED: "parlay_calculated",
  ODDS_CONVERTED: "odds_converted",
  RESULTS_COPIED: "calculator_results_copied",
  RESULTS_SHARED: "calculator_results_shared",
  PARLAY_LEG_ADDED: "parlay_leg_added",
  PARLAY_LEG_REMOVED: "parlay_leg_removed",
  // Playbook
  PLAYBOOK_OPENED: "playbook_opened",
  PLAYBOOK_LESSON_OPENED: "playbook_lesson_opened",
  PLAYBOOK_SEARCH_USED: "playbook_search_used",
  PLAYBOOK_QUIZ_ANSWERED: "playbook_quiz_answered",
  PLAYBOOK_LESSON_COMPLETED: "playbook_lesson_completed",
  PLAYBOOK_RELATED_TOOL_CLICKED: "playbook_related_tool_clicked",
  PLAYBOOK_NEXT_LESSON_CLICKED: "playbook_next_lesson_clicked",
  PLAYBOOK_PREVIOUS_LESSON_CLICKED: "playbook_previous_lesson_clicked",
  // Round Robin
  ROUND_ROBIN_OPENED: "round_robin_calculator_opened",
  ROUND_ROBIN_LEG_ADDED: "round_robin_leg_added",
  ROUND_ROBIN_LEG_REMOVED: "round_robin_leg_removed",
  ROUND_ROBIN_SIZE_TOGGLED: "round_robin_size_toggled",
  ROUND_ROBIN_WAGER_MODE_CHANGED: "round_robin_wager_mode_changed",
  ROUND_ROBIN_CALCULATED: "round_robin_calculated",
  ROUND_ROBIN_STATUS_CHANGED: "round_robin_leg_status_changed",
  ROUND_ROBIN_RESET: "round_robin_reset",
  // Homepage discovery
  PLAYBOOK_DISCOVERY_CARD_CLICKED: "playbook_discovery_card_clicked",
  // Prop Odds
  PROP_ODDS_PAGE_VIEWED: "prop_odds_page_viewed",
  PROP_GAME_SELECTED: "prop_game_selected",
  PROP_TYPE_SELECTED: "prop_type_selected",
  PROP_PLAYER_SEARCHED: "prop_player_searched",
  PROP_GAME_EXPANDED: "prop_game_expanded",
  PROP_CATEGORY_SELECTED: "prop_category_selected",
  PROP_PLAYER_EXPANDED: "prop_player_expanded",
  SPORTSBOOK_LINK_CLICKED: "sportsbook_link_clicked",
};
